/**
 * Parse CSV text into a header row and data rows.
 *
 * Supports quoted fields (so commas and line breaks inside `"..."` are kept),
 * escaped quotes (`""`), and both `\n` and `\r\n` line endings.
 *
 * @param {string} text Raw CSV content.
 * @returns {{ headers: string[], rows: string[][] }}
 */
export const parseCsv = (text = '') => {
  const records = [];
  let field = '';
  let record = [];
  let inQuotes = false;

  const pushField = () => {
    record.push(field);
    field = '';
  };

  const pushRecord = () => {
    pushField();
    records.push(record);
    record = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (inQuotes) {
      if (char !== '"') {
        field += char;
      } else if (text[i + 1] === '"') {
        // Escaped quote: consume the pair, keep a single literal quote.
        field += '"';
        i += 1;
      } else {
        inQuotes = false;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      pushField();
    } else if (char === '\n') {
      pushRecord();
    } else if (char === '\r') {
      // Swallow CR; the following LF (if any) ends the record.
      if (text[i + 1] !== '\n') {
        pushRecord();
      }
    } else {
      field += char;
    }
  }

  // Flush the trailing field/record unless the file ends with a newline.
  if (field !== '' || record.length) {
    pushRecord();
  }

  // Drop fully empty trailing records (e.g. file ending with a blank line).
  const nonEmpty = records.filter(row => !(row.length === 1 && row[0] === ''));

  if (!nonEmpty.length) {
    return { headers: [], rows: [] };
  }

  const [headers, ...rows] = nonEmpty;

  return { headers, rows };
};
